
import React, { useState } from 'react';
import { File, ArrowLeft, Check, Loader2, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StandardizedConnection } from '@/types/connections';
import UnifiedCsvImport from '@/components/connectors/UnifiedCsvImport';
import { supabase } from '@/integrations/supabase/client';
import { Json } from '@/integrations/supabase/types';

interface CsvImportStepProps {
  onBack: () => void;
  onSubmit: () => void;
  onSaveAsDraft?: () => void;
  isSubmitting: boolean;
  isSavingDraft?: boolean;
  csvConnectors: StandardizedConnection[];
  setCsvConnectors: (connectors: StandardizedConnection[]) => void;
  showSaveDraftButton?: boolean;
  organizationId?: string;
}

const CsvImportStep: React.FC<CsvImportStepProps> = ({
  onBack,
  onSubmit,
  onSaveAsDraft,
  isSubmitting,
  isSavingDraft = false,
  csvConnectors,
  setCsvConnectors,
  showSaveDraftButton = false,
  organizationId
}) => {
  const [isImporting, setIsImporting] = useState(false);

  const handleCsvSuccess = async (connector: StandardizedConnection) => {
    console.info('[csv-org-debug] CsvImportStep.handleCsvSuccess', {
      organizationId,
      connector: {
        id: connector.id,
        connectionId: connector.connectionId,
        connectorId: connector.connectorId,
        file_name: connector.file_name,
        import_type: connector.import_type,
        ecommerce_subtype: connector.ecommerce_subtype,
        idImpliesShopify: String(connector.id || '').toLowerCase().includes('_inventory_shopify_'),
        idImpliesAmazon: String(connector.id || '').toLowerCase().includes('_inventory_amazon_'),
      },
      currentCsvConnectorsCount: csvConnectors.length,
    });
    setCsvConnectors([...csvConnectors, connector]);
    setIsImporting(false);

    // Immediately save the connection to the organisation record
    if (organizationId) {
      try {
        // Get current organisation data
        const { data: currentOrg, error: fetchError } = await supabase
          .from('organisations')
          .select('connections')
          .eq('id', organizationId)
          .single();

        if (fetchError) {
          console.error('Error fetching current organisation:', fetchError);
          return;
        }

        // Add new connection to existing connections
        const existingConnections = Array.isArray(currentOrg?.connections) ? currentOrg.connections : [];
        const updatedConnections = [...existingConnections, connector];

        // Update organisation with new connection
        const { error: updateError } = await supabase
          .from('organisations')
          .update({ connections: updatedConnections as unknown as Json })
          .eq('id', organizationId);

        if (updateError) {
          console.error('Error updating organisation connections:', updateError);
        }
      } catch (error) {
        console.error('Error saving connection to organisation:', error);
      }
    }
  };

  const removeConnector = async (connectorId: string) => {
    setCsvConnectors(csvConnectors.filter(c => c.id !== connectorId));

    // Also remove from organisation record if organizationId exists
    if (organizationId) {
      try {
        // Get current organisation data
        const { data: currentOrg, error: fetchError } = await supabase
          .from('organisations')
          .select('connections')
          .eq('id', organizationId)
          .single();

        if (fetchError) {
          console.error('Error fetching current organisation for removal:', fetchError);
          return;
        }

        // Remove connection from existing connections
        const existingConnections = Array.isArray(currentOrg?.connections) ? currentOrg.connections : [];
        const updatedConnections = existingConnections.filter((conn: any) => conn.id !== connectorId);

        // Update organisation with filtered connections
        const { error: updateError } = await supabase
          .from('organisations')
          .update({ connections: updatedConnections as unknown as Json })
          .eq('id', organizationId);

        if (updateError) {
          console.error('Error removing connection from organisation:', updateError);
        }
      } catch (error) {
        console.error('Error removing connection from organisation:', error);
      }
    }
  };

  const handleImportStart = () => {
    setIsImporting(true);
  };

  const canSubmit = csvConnectors.length > 0;

  return (
    <div data-testid="csv-import-step">
      <div className="space-y-4">
        <div className="text-sm text-muted-foreground mb-4">
          Import your data from e-commerce platforms (Amazon, Shopify, etc.), banking, or accounting systems.
        </div>

        {csvConnectors.length > 0 ? (
          <div className="mb-6">
            <div className="space-y-3 mb-4">
              {csvConnectors.map((connector, index) => (
                <div key={connector.id} className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="import-success-card">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start">
                      <div className="mr-3 mt-0.5">
                        <Check className="h-5 w-5 text-green-500" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-green-700">File {index + 1} Imported Successfully</h3>
                        <p className="text-green-600 mt-1">
                          <span className="font-medium">{connector.file_name}</span>
                        </p>
                        <div className="flex items-center mt-2">
                          <File className="h-4 w-4 text-gray-500 mr-2" />
                          <span className="text-sm text-gray-600">{connector.file_name}</span>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeConnector(connector.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-700 mb-3">Import Additional Files</h4>
              <UnifiedCsvImport
                onSuccess={handleCsvSuccess}
                onImportStart={handleImportStart}
                isConnecting={isImporting}
                shouldSaveDraft={false}
                mode="connector"
                organizationId={organizationId}
              />
            </div>
          </div>
        ) : (
          <div className="py-2">
            <UnifiedCsvImport
              onSuccess={handleCsvSuccess}
              onImportStart={handleImportStart}
              isConnecting={isImporting}
              shouldSaveDraft={false}
              mode="connector"
              organizationId={organizationId}
            />
          </div>
        )}
      </div>

      <div className="flex justify-between mt-6 border-t pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="flex items-center"
          disabled={isSubmitting || isSavingDraft}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <div className="flex space-x-3">
          {csvConnectors.length > 0 && (
            <Button
              type="button"
              variant="outline"
              onClick={async () => {
                // Clear from organisation record if organizationId exists
                if (organizationId) {
                  try {
                    // Get current organisation data
                    const { data: currentOrg, error: fetchError } = await supabase
                      .from('organisations')
                      .select('connections')
                      .eq('id', organizationId)
                      .single();

                    if (!fetchError && currentOrg) {
                      // Remove all CSV connections
                      const existingConnections = Array.isArray(currentOrg.connections) ? currentOrg.connections : [];
                      const csvConnectorIds = csvConnectors.map(c => c.id);
                      const filteredConnections = existingConnections.filter((conn: any) => !csvConnectorIds.includes(conn.id));

                      // Update organisation with filtered connections
                      const { error: updateError } = await supabase
                        .from('organisations')
                        .update({ connections: filteredConnections as unknown as Json })
                        .eq('id', organizationId);

                      if (updateError) {
                        console.error('Error clearing connections from organisation:', updateError);
                      }
                    }
                  } catch (error) {
                    console.error('Error clearing connections from organisation:', error);
                  }
                }

                setCsvConnectors([]);
                setIsImporting(false);
              }}
              disabled={isSubmitting || isSavingDraft}
            >
              Clear All Files
            </Button>
          )}

          {showSaveDraftButton && onSaveAsDraft && (
            <Button
              type="button"
              variant="secondary"
              onClick={onSaveAsDraft}
              disabled={isSubmitting || isSavingDraft}
              className="flex items-center"
            >
              {isSavingDraft ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save as Draft
                </>
              )}
            </Button>
          )}

          <Button
            type="button"
            onClick={onSubmit}
            disabled={isSubmitting || isSavingDraft || !canSubmit}
            className="bg-brand-primary hover:bg-brand-primary/90"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Organisation'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CsvImportStep;
